import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { aiMonitoringAPI } from '../../services';
import RegionFrameEditor from '../../components/region-splits/RegionFrameEditor';
import { colorOf, toWire, validateFrames } from '../../utils/regionFrameGeometry';

/**
 * #223029 — KHOANH VÙNG trên ảnh camera (app Admin, lệnh sếp 17/10: phần dành cho admin làm ở admin,
 * không làm ở app nhân viên). Chọn camera → ảnh mẫu → vẽ khung (chữ nhật / đa giác) → gán "Bàn khách"
 * (số bàn KHÔNG bắt buộc), "Bàn con" hoặc "Không phục vụ" → lưu. Hệ thống theo dõi đúng các vùng đã khoanh.
 */
let seq = 0;
const newKey = () => `k${Date.now().toString(36)}${seq++}`;

const fromWire = (f) => ({
  key: newKey(),
  hinh: f.hinh,
  diem: f.diem.map((p) => [p[0], p[1]]),
  ten: f.ten,
  loai: f.loai,
  ban_that: f.ban_that ?? null,
  thuoc_ban_that: f.thuoc_ban_that ?? null,
  mau: f.mau ?? null,
});

const LOAI_LABEL = {
  ban_that: 'Bàn khách',
  ban_con: 'Bàn con',
  khong_phuc_vu: 'Không phục vụ (giữ xe / quầy / nhân viên)',
};
const LOAI_MOTA = {
  ban_that: 'Một khu khách ngồi (có thể gắn số bàn hoặc để trống)',
  ban_con: 'Tách một bàn lớn thành nhóm khách riêng (vd Sofa trái)',
  khong_phuc_vu: 'Giữ xe, quầy, chỗ nhân viên — không đếm là khách',
};

const fmtTime = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('vi-VN');
};
const moTaKhungDaLuu = (c) =>
  [`${c.so_khung} khung`, fmtTime(c.luu_luc) ? `lưu ${fmtTime(c.luu_luc)}` : '', c.nguoi_luu || '']
    .filter(Boolean)
    .join(' · ');


// Hàng đợi tải ảnh đại diện: tối đa 3 ảnh cùng lúc để không dồn dịch vụ camera.
const thumbQueue = [];
let thumbRunning = 0;
const startThumbJob = (job) => {
  thumbRunning += 1;
  job().finally(() => {
    thumbRunning -= 1;
    // eslint-disable-next-line no-use-before-define
    runThumbQueue();
  });
};
const runThumbQueue = () => {
  while (thumbRunning < 3 && thumbQueue.length) startThumbJob(thumbQueue.shift());
};
const enqueueThumb = (job) => {
  thumbQueue.push(job);
  runThumbQueue();
};

const centroid = (diem) => [
  diem.reduce((a, p) => a + p[0], 0) / diem.length,
  diem.reduce((a, p) => a + p[1], 0) / diem.length,
];

/** Thẻ camera: ảnh MỚI NHẤT làm ảnh đại diện + các khung đã khoanh vẽ đè (đánh số) để nhìn nhanh biết camera nào, khoanh mấy vùng. */
function CameraCard({ c, branch, onOpen }) {
  const ref = useRef(null);
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const draft = useMemo(() => (c.khung || []).map(fromWire), [c.khung]);

  useEffect(() => {
    if (!c.co_anh) return undefined;
    let cancelled = false;
    let objectUrl = null;
    const el = ref.current;
    const load = () =>
      enqueueThumb(async () => {
        if (cancelled) return;
        try {
          const blob = await aiMonitoringAPI.getRegionCameraImage(c.serial, branch, c.anh_ngay, c.anh_gio);
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        } catch (e) {
          if (!cancelled) setFailed(true);
        }
      });
    let io = null;
    if (el && typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((x) => x.isIntersecting)) {
            io.disconnect();
            load();
          }
        },
        { rootMargin: '200px' },
      );
      io.observe(el);
    } else {
      load();
    }
    return () => {
      cancelled = true;
      if (io) io.disconnect();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [c.serial, c.anh_ngay, c.anh_gio, c.co_anh, branch]);

  return (
    <Card
      role="button"
      data-testid={`camera-${c.serial}`}
      onClick={() => onOpen(c)}
      className="overflow-hidden"
      style={{ cursor: 'pointer' }}
    >
      <div
        ref={ref}
        className="position-relative bg-dark"
        style={{ aspectRatio: '16 / 9' }}
        data-testid={`anh-dai-dien-${c.serial}`}
      >
        {url ? (
          <img
            src={url}
            alt={c.ten}
            draggable={false}
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{ objectFit: 'fill' }}
          />
        ) : (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-white-50 small">
            {!c.co_anh || failed ? 'Chưa có ảnh' : <Spinner animation="border" size="sm" />}
          </div>
        )}
        {url && (
          <svg
            className="position-absolute top-0 start-0 w-100 h-100"
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            style={{ pointerEvents: 'none' }}
          >
            {draft.map((f, i) => {
              const col = colorOf(f, i);
              const excl = f.loai === 'khong_phuc_vu';
              return (
                <path
                  key={f.key}
                  d={f.diem.map((q, k) => `${k ? 'L' : 'M'}${q[0]} ${q[1]}`).join(' ') + ' Z'}
                  fill={col}
                  fillOpacity={excl ? 0.3 : 0.22}
                  stroke={col}
                  strokeDasharray={excl ? '0.012 0.008' : undefined}
                  vectorEffect="non-scaling-stroke"
                  style={{ strokeWidth: 2 }}
                />
              );
            })}
          </svg>
        )}
        {url &&
          draft.map((f, i) => {
            const [cx, cy] = centroid(f.diem);
            return (
              <span
                key={f.key}
                className="position-absolute d-flex align-items-center justify-content-center text-white fw-bold"
                style={{
                  left: `${cx * 100}%`,
                  top: `${cy * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  fontSize: 11,
                  background: colorOf(f, i),
                  border: '1px solid #fff',
                  pointerEvents: 'none',
                }}
              >
                {i + 1}
              </span>
            );
          })}
        <Badge
          bg={c.da_khoanh ? 'success' : 'secondary'}
          className="position-absolute"
          style={{ top: 6, right: 6 }}
        >
          {c.da_khoanh ? `${c.so_khung} vùng` : 'Chưa khoanh'}
        </Badge>
      </div>
      <Card.Body className="py-2">
        <div className="fw-semibold text-truncate">{c.ten}</div>
        <div className="small text-muted text-truncate">
          {c.ban?.length ? `bàn ${c.ban.map((b) => b.so_ban).join(', ')}` : 'chưa gán bàn'}
          {c.gio_chup ? ` · ảnh ${c.gio_chup}` : ''}
        </div>
        {c.da_khoanh && <div className="small text-muted text-truncate">{moTaKhungDaLuu(c)}</div>}
      </Card.Body>
    </Card>
  );
}

export default function AiRegionSplitPage() {
  const { selectedBranch } = useBranch();
  const branch = selectedBranch?.id;

  const [list, setList] = useState(null);
  const [listError, setListError] = useState(null);
  const [loadingList, setLoadingList] = useState(false);

  const [cam, setCam] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [imageSize, setImageSize] = useState(null);
  const [frames, setFrames] = useState([]);
  const undoStack = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [selected, setSelected] = useState(-1);
  const [mode, setMode] = useState('select');
  const [pending, setPending] = useState(null);
  const [saved, setSaved] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [confirmSave, setConfirmSave] = useState(false);
  const [history, setHistory] = useState(null);

  const draftKey = cam ? `khoanh-nhap:${branch}:${cam.serial}` : null;

  const loadList = useCallback(async () => {
    if (!branch) return;
    setLoadingList(true);
    setListError(null);
    try {
      setList(await aiMonitoringAPI.listRegionCameras(branch));
    } catch (e) {
      setListError(e?.message || 'Không tải được danh sách camera');
    } finally {
      setLoadingList(false);
    }
  }, [branch]);

  useEffect(() => {
    setCam(null);
    setList(null);
    loadList();
  }, [loadList]);

  const commit = useCallback(
    (next) => {
      undoStack.current.push(frames);
      if (undoStack.current.length > 50) undoStack.current.shift();
      setCanUndo(true);
      setFrames(next);
      setDirty(true);
      setSaveMsg(null);
    },
    [frames],
  );

  const undo = () => {
    const prev = undoStack.current.pop();
    if (!prev) return;
    setFrames(prev);
    setCanUndo(undoStack.current.length > 0);
    setSelected(-1);
    setDirty(true);
  };

  // nháp tự giữ trong tab: đổi tab / mất mạng không mất khung đang vẽ
  useEffect(() => {
    if (!draftKey || !dirty) return;
    try {
      sessionStorage.setItem(draftKey, JSON.stringify(frames));
    } catch (e) {
      /* đầy bộ nhớ: bỏ qua */
    }
  }, [draftKey, frames, dirty]);

  const loadImage = useCallback(
    async (c, latest = false) => {
      setImageError(null);
      try {
        const blob = await aiMonitoringAPI.getRegionCameraImage(
          c.serial,
          branch,
          latest ? null : c.anh_ngay,
          latest ? null : c.anh_gio,
        );
        setImageUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return URL.createObjectURL(blob);
        });
      } catch (e) {
        setImageUrl(null);
        setImageError(
          e?.status === 404
            ? 'Camera chưa có ảnh mới. Thử “Lấy ảnh mới” sau ít phút.'
            : e?.status === 403
              ? 'Tài khoản này chưa được cấp quyền xem ảnh camera.'
              : e?.message || 'Không tải được ảnh camera',
        );
      }
    },
    [branch],
  );

  const openCamera = async (c) => {
    setCam(c);
    setSelected(-1);
    setMode('select');
    setSaveMsg(null);
    setHistory(null);
    setConfirmSave(false);
    undoStack.current = [];
    setCanUndo(false);
    setDirty(false);
    setFrames([]);
    setImageUrl(null);
    loadImage(c);
    try {
      const set = await aiMonitoringAPI.getRegionSplit(c.serial, branch);
      setSaved(set);
      let draft = null;
      try {
        const raw = sessionStorage.getItem(`khoanh-nhap:${branch}:${c.serial}`);
        if (raw) draft = JSON.parse(raw);
      } catch (e) {
        draft = null;
      }
      if (draft && draft.length) {
        setFrames(draft);
        setDirty(true);
        setSaveMsg({ ok: false, text: 'Đã khôi phục khung bạn đang vẽ dở trong tab này — chưa lưu.' });
      } else {
        setFrames((set?.frames ?? []).map(fromWire));
      }
    } catch (e) {
      setSaved(null);
      setSaveMsg({ ok: false, text: e?.message || 'Không tải được khung đã lưu' });
    }
  };

  const closeCamera = () => {
    if (dirty && !window.confirm('Khung đang vẽ chưa lưu sẽ giữ tạm trong tab này. Thoát?')) return;
    setCam(null);
    setImageUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return null;
    });
    loadList();
  };

  const applyPending = (f) => {
    if (!pending) return;
    if (pending.kind === 'new') {
      commit([...frames, { key: newKey(), hinh: pending.hinh, diem: pending.diem, ...f }]);
      setSelected(frames.length);
    } else {
      commit(frames.map((x, i) => (i === pending.index ? { ...x, ...f } : x)));
    }
    setPending(null);
    setMode('select');
  };

  const removeFrame = (i) => {
    commit(frames.filter((_, k) => k !== i));
    setSelected(-1);
    setPending(null);
  };

  const doSave = async () => {
    if (!cam) return;
    const err = validateFrames(frames);
    if (err) {
      setSaveMsg({ ok: false, text: err });
      setConfirmSave(false);
      return;
    }
    setSaving(true);
    setSaveMsg(null);
    try {
      const gio =
        cam.anh_gio && cam.anh_gio.length >= 4 ? `${cam.anh_gio.slice(0, 2)}:${cam.anh_gio.slice(2, 4)}` : null;
      const set = await aiMonitoringAPI.saveRegionSplit({
        branch_id: branch,
        camera_serial: cam.serial,
        frames: frames.map(toWire),
        khung_anh_mau: { ngay: cam.anh_ngay, gio, rong: imageSize?.w, cao: imageSize?.h },
      });
      setSaved(set);
      setFrames(set.frames.map(fromWire));
      setDirty(false);
      undoStack.current = [];
      setCanUndo(false);
      if (draftKey) sessionStorage.removeItem(draftKey);
      setConfirmSave(false);
      setSaveMsg({
        ok: true,
        text: set.frames.length
          ? `Đã lưu bản ${set.version} (${set.frames.length} khung). Áp dụng từ lượt quét kế tiếp (≤ 5 phút).`
          : `Đã xoá hết khung (bản ${set.version}). Camera quay về tự gom nhóm.`,
      });
    } catch (e) {
      setConfirmSave(false);
      setSaveMsg({
        ok: false,
        text:
          e?.status === 403
            ? 'Tài khoản này không có quyền lưu khung.'
            : `Chưa lưu được (${e?.message || 'lỗi mạng'}). Khung của bạn vẫn còn — bấm Lưu lại.`,
      });
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async () => {
    if (!cam) return;
    try {
      setHistory(await aiMonitoringAPI.getRegionSplitHistory(cam.serial, branch));
    } catch (e) {
      setSaveMsg({ ok: false, text: e?.message || 'Không tải được lịch sử' });
    }
  };

  const restore = async (v) => {
    if (!window.confirm(`Khôi phục bản ${v.version}? Bản đang áp sẽ chuyển về nội dung bản ${v.version} (bản hiện tại vẫn giữ trong lịch sử).`)) return;
    try {
      const set = await aiMonitoringAPI.restoreRegionSplit(v.id, branch);
      setSaved(set);
      setFrames(set.frames.map(fromWire));
      setDirty(false);
      if (draftKey) sessionStorage.removeItem(draftKey);
      setSaveMsg({ ok: true, text: `Đã khôi phục bản ${set.version} — đang áp dụng từ lượt quét kế tiếp.` });
      setHistory(await aiMonitoringAPI.getRegionSplitHistory(set.camera_serial, branch));
    } catch (e) {
      setSaveMsg({ ok: false, text: e?.message || 'Không khôi phục được' });
    }
  };

  const excludedFrames = useMemo(
    () => frames.map((f, i) => ({ f, i })).filter((x) => x.f.loai === 'khong_phuc_vu'),
    [frames],
  );
  const canSave = dirty || frames.length !== (saved?.frames.length ?? 0);

  return (
    <PageLayout>
      <div className="mc-card">
        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
          <h4 className="mb-0">Khoanh vùng camera</h4>
          {cam ? (
            <Button variant="outline-secondary" size="sm" onClick={closeCamera}>
              ← Danh sách camera
            </Button>
          ) : (
            <Button variant="outline-primary" size="sm" onClick={loadList} disabled={loadingList || !branch}>
              {loadingList ? <Spinner size="sm" animation="border" /> : 'Tải lại'}
            </Button>
          )}
        </div>
        <p className="small text-muted mb-3">
          {selectedBranch?.name ? `${selectedBranch.name} · ` : ''}
          {cam
            ? cam.ten
            : 'Chọn camera, rồi khoanh các khu vực cần theo dõi. Hệ thống chỉ theo dõi theo các vùng đã khoanh.'}
        </p>

        {!branch && <p className="text-muted">Chọn chi nhánh để xem camera.</p>}

        {/* ---------- BƯỚC 1: chọn camera ---------- */}
        {branch && !cam && (
          <>
            {loadingList && <Spinner animation="border" size="sm" />}
            {listError && (
              <Alert variant="danger" className="py-2">
                {listError}
              </Alert>
            )}
            {list && !list.connected && (
              <Alert variant="warning" className="py-2" data-testid="chua-noi-cam">
                Chưa nối được dịch vụ camera nên chưa có camera nào để chọn. Báo kỹ thuật kiểm tra kết nối camera.
              </Alert>
            )}
            {list && list.connected && list.cameras.length === 0 && (
              <p className="text-muted">Chi nhánh này chưa có camera nào.</p>
            )}
            {list && list.connected && !list.khop_chi_nhanh && list.cameras.length > 0 && (
              <p className="small text-muted">Mã chi nhánh trên camera khác mã chi nhánh của ứng dụng, nên hiện tất cả camera.</p>
            )}
            <div className="d-grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
              {list?.cameras.map((c) => (
                <CameraCard key={c.serial} c={c} branch={branch} onOpen={openCamera} />
              ))}
            </div>
          </>
        )}

        {/* ---------- BƯỚC 2: ảnh mẫu + khoanh ---------- */}
        {cam && (
          <div className="row g-3">
            <div className="col-lg-8">
              {imageError && (
                <Alert variant="danger" className="py-2" data-testid="loi-anh">
                  {imageError}{' '}
                  <Button size="sm" variant="outline-danger" onClick={() => loadImage(cam, true)}>
                    Lấy ảnh mới
                  </Button>
                </Alert>
              )}
              {!imageUrl && !imageError && (
                <div className="d-flex align-items-center justify-content-center border rounded bg-light" style={{ aspectRatio: '16/9' }}>
                  <Spinner animation="border" size="sm" />
                </div>
              )}
              {imageUrl && (
                <RegionFrameEditor
                  imageUrl={imageUrl}
                  frames={frames}
                  selected={selected}
                  mode={mode}
                  readOnly={false}
                  onModeChange={setMode}
                  onSelect={setSelected}
                  onDrawn={(hinh, diem) => setPending({ kind: 'new', hinh, diem })}
                  onEditStart={() => {
                    // Mỗi lần kéo (dời khung / đổi cỡ / kéo đỉnh) là 1 bước Hoàn tác.
                    undoStack.current.push(frames);
                    if (undoStack.current.length > 50) undoStack.current.shift();
                    setCanUndo(true);
                  }}
                  onReplacePoints={(fi, diem) => {
                    setDirty(true);
                    setSaveMsg(null);
                    setFrames((cur) => cur.map((f, i) => (i === fi ? { ...f, diem } : f)));
                  }}
                  onUndo={undo}
                  canUndo={canUndo}
                  onImageSize={(w, h) => setImageSize({ w, h })}
                  onRequestSave={() => setConfirmSave(true)}
                  canSave={!saving && canSave}
                />
              )}
              <div className="d-flex justify-content-between small text-muted mt-2">
                <span>
                  {cam.gio_chup ? `Ảnh chụp lúc ${cam.gio_chup}` : ''}
                  {saved ? ` · đang áp bản ${saved.version}` : ' · chưa có bản nào'}
                </span>
                <Button variant="link" size="sm" className="p-0" onClick={() => loadImage(cam, true)}>
                  Lấy ảnh mới
                </Button>
              </div>
            </div>

            <div className="col-lg-4">
              <h6>Các khung ({frames.length})</h6>
              {frames.length === 0 && (
                <p className="small text-muted border rounded p-2">
                  Chưa có khung. Chọn “Chữ nhật” hoặc “Đa giác” rồi vẽ lên ảnh.
                </p>
              )}
              <div className="d-grid gap-2" data-testid="ds-khung">
                {frames.map((f, i) => (
                  <div
                    key={f.key}
                    data-testid={`dong-khung-${i}`}
                    className={`d-flex align-items-center gap-2 border rounded p-2 ${i === selected ? 'border-primary' : ''}`}
                  >
                    <span style={{ width: 14, height: 14, borderRadius: 3, background: colorOf(f, i), flex: '0 0 auto' }} />
                    <div className="flex-grow-1" style={{ minWidth: 0, cursor: 'pointer' }} onClick={() => setSelected(i)}>
                      <div className="fw-semibold text-truncate">{f.ten}</div>
                      <div className="small text-muted">
                        {LOAI_LABEL[f.loai]}
                        {f.loai === 'ban_that' && f.ban_that ? ` · bàn ${f.ban_that}` : ''}
                        {f.loai === 'ban_con' && f.thuoc_ban_that ? ` · của bàn ${f.thuoc_ban_that}` : ''}
                      </div>
                    </div>
                    <Button size="sm" variant="outline-secondary" onClick={() => { setSelected(i); setPending({ kind: 'edit', index: i }); }}>
                      Sửa
                    </Button>
                    <Button size="sm" variant="outline-danger" aria-label={`Xoá ${f.ten}`} onClick={() => removeFrame(i)}>
                      ✕
                    </Button>
                  </div>
                ))}
              </div>

              {excludedFrames.length > 0 && (
                <div className="border rounded p-2 mt-3 bg-light" data-testid="vung-da-loai">
                  <h6 className="mb-2">Khu đã loại — không phải bàn khách ({excludedFrames.length})</h6>
                  {excludedFrames.map(({ f, i }) => (
                    <div key={f.key} className="d-flex align-items-center gap-2 mb-1">
                      <span className="flex-grow-1 text-truncate">{f.ten}</span>
                      <Button size="sm" variant="outline-secondary" data-testid={`mo-lai-${i}`} onClick={() => { setSelected(i); setPending({ kind: 'edit', index: i }); }}>
                        Mở lại
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {saveMsg && (
                <Alert variant={saveMsg.ok ? 'success' : 'danger'} className="py-2 mt-3 mb-0" data-testid="thong-bao-luu">
                  {saveMsg.text}
                </Alert>
              )}

              <div className="d-grid gap-2 mt-3">
                <Button disabled={saving || !canSave} data-testid="nut-luu" onClick={() => setConfirmSave(true)}>
                  {saving ? <Spinner size="sm" animation="border" /> : 'Lưu'}
                </Button>
                <Button variant="outline-secondary" onClick={openHistory} data-testid="mo-lich-su">
                  Lịch sử các bản đã lưu
                </Button>
              </div>

              {history && (
                <div className="d-grid gap-2 mt-3" data-testid="lich-su">
                  {history.length === 0 && <p className="small text-muted mb-0">Chưa có bản nào.</p>}
                  {history.map((v) => (
                    <div key={v.id} className="d-flex align-items-center gap-2 border rounded p-2 small">
                      <div className="flex-grow-1">
                        <b>Bản {v.version}</b>
                        {v.active ? ' · đang áp' : ''}
                        <div className="text-muted">
                          {v.frames.length} khung · {fmtTime(v.saved_at)}
                        </div>
                      </div>
                      <Button size="sm" variant="outline-secondary" onClick={() => { setFrames(v.frames.map(fromWire)); setDirty(true); setSelected(-1); }}>
                        Xem
                      </Button>
                      {!v.active && (
                        <Button size="sm" data-testid={`khoi-phuc-${v.version}`} onClick={() => restore(v)}>
                          Khôi phục
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ---------- xác nhận lưu ---------- */}
      <Modal show={confirmSave} onHide={() => setConfirmSave(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Lưu bộ khung cho {cam?.ten}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {frames.length} khung. Áp dụng từ lượt quét kế tiếp (≤ 5 phút). Bản cũ vẫn giữ trong lịch sử, khôi phục được.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmSave(false)}>
            Để sau
          </Button>
          <Button data-testid="xac-nhan-luu" disabled={saving} onClick={doSave}>
            {saving ? <Spinner size="sm" animation="border" /> : 'Lưu và áp dụng'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---------- đặt tên / loại ---------- */}
      {pending && cam && (
        <FrameForm
          key={pending.kind === 'edit' ? pending.index : 'new'}
          cam={cam}
          initial={pending.kind === 'edit' ? frames[pending.index] : null}
          reopen={pending.kind === 'edit' && frames[pending.index]?.loai === 'khong_phuc_vu'}
          existingNames={frames
            .filter((_, i) => !(pending.kind === 'edit' && i === pending.index))
            .map((f) => f.ten)}
          onCancel={() => setPending(null)}
          onOk={applyPending}
          onDelete={pending.kind === 'edit' ? () => removeFrame(pending.index) : undefined}
        />
      )}
    </PageLayout>
  );
}

function FrameForm({ cam, initial, reopen, existingNames, onCancel, onOk, onDelete }) {
  const startLoai = reopen ? 'ban_that' : initial?.loai ?? 'ban_that';
  const [loai, setLoai] = useState(startLoai);
  // Số bàn KHÔNG bắt buộc: mặc định "Chưa gắn bàn".
  const [ban, setBan] = useState(initial?.ban_that ?? initial?.thuoc_ban_that ?? null);
  const defaultName = (l, b) =>
    l === 'khong_phuc_vu'
      ? 'Không phục vụ'
      : l === 'ban_that'
        ? b
          ? `Bàn ${b}`
          : `Khu vực ${existingNames.length + 1}`
        : '';
  const [ten, setTen] = useState(reopen ? defaultName('ban_that', ban) : initial?.ten ?? defaultName(startLoai, ban));
  const [touched, setTouched] = useState(!!initial && !reopen);
  const [err, setErr] = useState(null);

  const pick = (l) => {
    setLoai(l);
    if (!touched) setTen(defaultName(l, ban));
  };

  const submit = () => {
    const name = ten.trim();
    if (!name) return setErr('Nhập tên khung.');
    if (existingNames.includes(name)) return setErr('Đã có khung trùng tên này — đặt tên khác.');
    onOk({
      ten: name,
      loai,
      ban_that: loai === 'ban_that' ? ban : undefined,
      thuoc_ban_that: loai === 'ban_con' ? ban : undefined,
      mau: initial?.mau ?? null,
    });
  };

  const banOptions = (cam.ban || []).map((b) => b.so_ban);
  const onBan = (v) => {
    setBan(v);
    if (!touched && loai === 'ban_that') setTen(defaultName('ban_that', v));
  };

  return (
    <Modal show onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>{reopen ? 'Mở lại khu này thành bàn khách' : initial ? 'Sửa khung' : 'Khung mới'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-grid gap-2 mb-3" role="radiogroup">
          {['ban_that', 'ban_con', 'khong_phuc_vu'].map((l) => (
            <button
              key={l}
              type="button"
              role="radio"
              aria-checked={loai === l}
              data-testid={`loai-${l}`}
              onClick={() => pick(l)}
              className={`btn text-start border ${loai === l ? 'border-primary bg-primary bg-opacity-10' : 'bg-white'}`}
            >
              <b>{LOAI_LABEL[l]}</b>
              <div className="small text-muted">{LOAI_MOTA[l]}</div>
            </button>
          ))}
        </div>

        {loai !== 'khong_phuc_vu' && (
          <Form.Group className="mb-3">
            <Form.Label>{loai === 'ban_that' ? 'Bàn số (không bắt buộc)' : 'Thuộc bàn số (không bắt buộc)'}</Form.Label>
            {banOptions.length > 0 ? (
              <Form.Select
                data-testid="chon-ban"
                value={ban ?? ''}
                onChange={(e) => onBan(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Chưa gắn bàn</option>
                {banOptions.map((n) => (
                  <option key={n} value={n}>
                    Bàn {n}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control
                type="number"
                min={1}
                data-testid="nhap-ban"
                placeholder="Để trống nếu chưa gắn bàn"
                value={ban ?? ''}
                onChange={(e) => onBan(e.target.value ? Number(e.target.value) : null)}
              />
            )}
          </Form.Group>
        )}

        <Form.Group>
          <Form.Label>Tên khung</Form.Label>
          <Form.Control
            data-testid="ten-khung"
            value={ten}
            maxLength={60}
            onChange={(e) => {
              setTen(e.target.value);
              setTouched(true);
            }}
          />
        </Form.Group>
        {err && <div className="text-danger small mt-2">{err}</div>}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <div>
          {onDelete && (
            <Button variant="outline-danger" onClick={onDelete}>
              Xoá khung này
            </Button>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={onCancel}>
            Huỷ
          </Button>
          <Button data-testid="luu-khung" onClick={submit}>
            {reopen ? 'Mở lại' : 'Xong'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
